// ./src/utils/api/PurchaseOrderService.ts
//
// Purchase-order service — account-level restock orders to a vendor (draft →
// ordered → received). Lines are nested (written via the order's `items` input).
// Managed from the account Inventory pages OR from an event's Inventory page: in
// the latter a `contextEventId` AUTH CONTEXT lets crew with event.operate manage
// the event account's orders. OpResult-native. See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Enum-like constants (mirror eventsmagic-api boundary/purchaseOrder.ts)
// =============================================================================

export const PURCHASE_ORDER_STATUS = {
  DRAFT: "draft",
  ORDERED: "ordered",
  RECEIVED: "received",
  CANCELED: "canceled",
} as const;

export const PURCHASE_ORDER_STATUS_VALUES = Object.values(PURCHASE_ORDER_STATUS);

export const PURCHASE_ORDER_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  ordered: "Ordered",
  received: "Received",
  canceled: "Canceled",
};

/** Badge color per status (Mantine palette keys). */
export const PURCHASE_ORDER_STATUS_COLORS: Record<string, string> = {
  draft: "gray",
  ordered: "blue",
  received: "green",
  canceled: "red",
};

// =============================================================================
// GraphQL
// =============================================================================

const PURCHASE_ORDER_FIELDS = `
  id
  accountId
  code
  vendorId
  vendorLabel
  vendor { id name }
  eventId
  event { id name }
  poStatus
  currencyCode
  expectedAt
  orderedAt
  receivedAt
  notes
  items {
    id
    inventoryItemId
    name
    quantity
    receivedQty
    unitCost
    notes
    orderNo
    status
  }
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const PURCHASE_ORDER_LIST_QUERY = gql`
  query PurchaseOrderList($filter: PurchaseOrderFilter, $params: PaginationAndSorting) {
    purchaseOrderList(filter: $filter, params: $params) {
      code
      total
      data { ${PURCHASE_ORDER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PURCHASE_ORDER_GET_QUERY = gql`
  query PurchaseOrderGet($id: ID) {
    purchaseOrderGet(id: $id) {
      code
      data { ${PURCHASE_ORDER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PURCHASE_ORDER_CREATE_MUTATION = gql`
  mutation PurchaseOrderCreate($params: PurchaseOrderInput) {
    purchaseOrderCreate(params: $params) {
      code
      data { ${PURCHASE_ORDER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PURCHASE_ORDER_UPDATE_MUTATION = gql`
  mutation PurchaseOrderUpdate($where: PurchaseOrderWhereInput, $params: PurchaseOrderInput) {
    purchaseOrderUpdate(where: $where, params: $params) {
      code
      data { ${PURCHASE_ORDER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PURCHASE_ORDER_REMOVE_MUTATION = gql`
  mutation PurchaseOrderRemove($where: PurchaseOrderWhereInput) {
    purchaseOrderRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api PurchaseOrder schema)
// =============================================================================

export interface PurchaseOrderItem {
  id?: string | null;
  inventoryItemId?: string | null;
  name?: string | null;
  quantity?: number | null;
  receivedQty?: number | null;
  unitCost?: number | null;
  notes?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

/**
 * A writable PO line (mirrors the SDL `PurchaseOrderItemInput`) — omits the read-only
 * `status`. `receivedQty` is optional: the buy-list sets it as it receives stock, while the
 * plain edit drawer OMITS it so editing a line never resets received progress (the server
 * leaves an absent field untouched on update).
 */
export interface PurchaseOrderItemInput {
  id?: string | null;
  inventoryItemId?: string | null;
  name?: string | null;
  quantity?: number | null;
  receivedQty?: number | null;
  unitCost?: number | null;
  notes?: string | null;
  orderNo?: number | null;
}

export interface PurchaseOrder {
  id?: string;
  accountId?: string | null;
  code?: string | null;
  vendorId?: string | null;
  vendorLabel?: string | null;
  vendor?: { id?: string | null; name?: string | null } | null;
  eventId?: string | null;
  event?: { id?: string | null; name?: string | null } | null;
  poStatus?: string | null;
  currencyCode?: string | null;
  expectedAt?: string | null;
  orderedAt?: string | null;
  receivedAt?: string | null;
  notes?: string | null;
  items?: PurchaseOrderItem[] | null;
  orderNo?: number | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PurchaseOrderInput {
  /** Auth context (NOT stored): the event page a crew member is acting from. */
  contextEventId?: string | null;
  vendorId?: string | null;
  eventId?: string | null;
  poStatus?: string | null;
  currencyCode?: string | null;
  expectedAt?: string | null;
  orderedAt?: string | null;
  receivedAt?: string | null;
  notes?: string | null;
  orderNo?: number | null;
  status?: number | null;
  /** Full desired line set, diffed on id server-side; omit to leave untouched. */
  items?: PurchaseOrderItemInput[] | null;
}

export interface PurchaseOrderFilter {
  id?: string[] | null;
  vendorId?: string[] | null;
  eventId?: string[] | null;
  poStatus?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
  contextEventId?: string | null;
}

export interface PurchaseOrderWhereInput {
  id?: string | string[];
  contextEventId?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class PurchaseOrderService {
  static async list(filter: PurchaseOrderFilter = {}, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(PURCHASE_ORDER_LIST_QUERY, {
      filter,
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Fetches EVERY order matching `filter` by paging the list — for whole-set consumers (the
   * reorder scan, export) that must cover the full filtered set rather than a single page.
   */
  static async listAll(
    filter: PurchaseOrderFilter = {},
    sortBy?: SortItem[],
    opts?: ListAllOptions
  ): Promise<OpResult> {
    return listAllPages<PurchaseOrder>(PurchaseOrderService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(PURCHASE_ORDER_GET_QUERY, { id });
  }

  static async create(params: PurchaseOrderInput): Promise<OpResult> {
    return ApiService.executeMutation(PURCHASE_ORDER_CREATE_MUTATION, { params });
  }

  static async update(where: PurchaseOrderWhereInput, params: PurchaseOrderInput): Promise<OpResult> {
    return ApiService.executeMutation(PURCHASE_ORDER_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string, contextEventId?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(PURCHASE_ORDER_REMOVE_MUTATION, {
      where: { id, contextEventId: contextEventId || undefined },
    });
  }

  static async removeMany(ids: string[], contextEventId?: string | null): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(PURCHASE_ORDER_REMOVE_MUTATION, {
      where: { id: ids, contextEventId: contextEventId || undefined },
    });
  }
}
